import { Modal } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { OUTLET_ACTIVITY_TYPES } from "../../../data/outlet-status-constants";
import { getFormattedDateTime } from "../../../helpers/text-format-helper";
import {
  getActivityDownload,
  getActivityErrorDetails,
  getActivityLogs,
  getCurrentSyncStatus,
  saveActivityErrorDetails,
  saveActivityLogs,
} from "../../../redux/slices";
import "./style.scss";
import { ToastNotifyError } from "../../../helpers/toast-helper";
import { cloneDeep } from "lodash";

interface Props {
  modalData: any;
  onModalClose: any;
}

const DownloadType = ["SYNC", "ITEM_OUT_OF_STOCK"];
const ActivityLogModal = (props: Props) => {
  const { modalData, onModalClose } = props;
  const { outletId, channelDefName } = modalData;
  const [activityLogsList, setActivityLogsList] = useState<any>([]),
    [activityErrorModalData, setActivityErrorModalData] = useState<any>({
      show: false,
      activityData: {},
    }),
    { singleOutletActivityLogs, activityErrorDetails } = useSelector(
      (state: any) => ({
        singleOutletActivityLogs:
          state.outletStatusReducer.singleOutletActivityLogs,
        activityErrorDetails: state.outletStatusReducer.activityErrorDetails,
      })
    );
  const dispatch = useDispatch();

  useEffect(() => {
    fetchActivityLogs(outletId);
    return () => {
      // clearing activity logs on component unmount
      dispatch(saveActivityLogs({}));
    };
  }, []);

  useEffect(() => {
    if (singleOutletActivityLogs?.results?.length) {
      setActivityLogsList((prevState: any) => [
        ...prevState,
        ...singleOutletActivityLogs?.results,
      ]);
    }
  }, [singleOutletActivityLogs]);

  const onViewDetailsClick = (activityData: any) => {
    dispatch(getActivityErrorDetails({ mid: activityData?.mid }));
    setActivityErrorModalData({ show: true, activityData });
  };

  const activityErrorModalClose = () => {
    setActivityErrorModalData({ show: false });
    dispatch(saveActivityErrorDetails({}));
  };

  const handleCheckStatus = (mid: string, currentIndex: number) => {
    const payload = {
      dataToPost: { activityId: mid },
      callback: (response: any) => {
        if (!response?.code) {
          const updatedActivityLog = cloneDeep(activityLogsList);
          if (updatedActivityLog[currentIndex]) {
            updatedActivityLog[currentIndex].externalStatus = response.externalStatus;
            updatedActivityLog[currentIndex].message = response.message;
          }
          setActivityLogsList(updatedActivityLog);
        }
        else {
          ToastNotifyError(response.code || 'Something went wrong')
        }
      }
    }
    dispatch(getCurrentSyncStatus(payload))
  }

  function handleDownloadClick(mid: string) {
    const payload = {
      mid,
      callback: (response: Record<string, string>) => {
        if (response.url) {
          window.location.href = response.url;
        }
      }
    }
    dispatch(getActivityDownload(payload));
  }

  function fetchActivityLogs(referenceId: string, lastKey: any = "") {
    let dataToPost: any = {
      group: "ORDER_AGGREGATORS",
      referenceId,
      pageSize: 20,
    };
    if (lastKey) {
      dataToPost.lastKey = lastKey;
    }
    dispatch(getActivityLogs({ dataToPost }));
  }

  function renderActivityInfo(activityData: any, index: number) {
    const statusConfig = {
      iconClass: "",
      colourClass: "",
      text: "",
    };

    // Pending
    if (activityData?.externalStatus === "Pending") {
      statusConfig.iconClass = "ic-info-yellow";
      statusConfig.colourClass = "text-[#E0A500]";
      statusConfig.text = "Pending";
    }
    // Success
    else if (activityData?.isSuccess) {
      statusConfig.iconClass = "ic-circle-tick-lightgreen";
      statusConfig.colourClass = "text-[#56B78B]";
      statusConfig.text = "Success";
    }
    // Failed
    else {
      statusConfig.iconClass = "ic-circle-cross-red";
      statusConfig.colourClass = "text-[#BF6666]";
      statusConfig.text = "Failed";
    }

    return (
      <div
        className="border-b border-[#EBEBEB] py-[10px] px-[20px] sm:px-[16px]"
        key={`activity-${activityData?.mid}`}
      >
        <div className="flex items-center">
          <div className="w-[60%] flex items-center sm:flex-col sm:items-start ">
            <div className="w-[calc(50%-12px)] mr-[12px] sm:w-full sm:mr-0">
              <p className="text-15 font-medium">
                {OUTLET_ACTIVITY_TYPES[activityData?.type] ||
                  activityData?.type}
              </p>
              <p className="text-12 text-black/50">
                {activityData?.message || ""}
              </p>
            </div>

            <p
              className={`text-12 text-black/50 w-[calc(50%-12px)] ml-[12px]
                sm:w-full sm:ml-0
              `}
            >
              {activityData?.userName ? `By ${activityData?.userName} ` : ""}
              {activityData?.lastModifiedDate
                ? `on ${getFormattedDateTime(activityData?.lastModifiedDate)}`
                : ""}
            </p>
          </div>

          <div
            className={`w-[calc(40%-20px)] ml-[20px] flex items-center sm:flex-col sm:items-end`}
          >
            <div className="flex items-center mr-[10px] w-[calc(50%-10px)] sm:w-auto sm:mr-0">
              <div
                className={`rista-sprite ${statusConfig.iconClass} shrink-0`}
              ></div>
              <p className={`text-12 ${statusConfig.colourClass} ml-[2px]`}>
                {statusConfig.text}
              </p>
            </div>
            <div className="flex items-center gap-[10px]">
              {DownloadType.includes(activityData.type) && <div onClick={() => handleDownloadClick(activityData.mid)} className={`rista-sprite ic-download-green ${activityData?.isSuccess ? 'mr-[15px]' : 'ml-[20px]'}`}>
              </div>}
              {(activityData.type === DownloadType[0] && channelDefName.toLowerCase().includes('swiggy') && activityData.externalStatus === 'Submitted') &&
                <div onClick={() => handleCheckStatus(activityData.mid, index)} className="rista-sprite ic-refresh-black ml-[10px] min-w-[20px] hover:rounded-[50%] hover:bg-[#DDDDDD]">
                </div>}
            </div>
            {activityData?.isSuccess ? null : (
              <div
                className={`w-[calc(50%-10px)] flex items-center justify-end cursor-pointer
                  sm:w-full sm:ml-0
                `}
                onClick={() => onViewDetailsClick(activityData)}
              >
                <div className="rista-sprite ic-view-details-eye-blue min-w-[20px]"></div>
                <p className="text-12 text-[#0074FB] ml-[2px] whitespace-nowrap">
                  View Details
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  function renderJsonErrorMessage(jsonMsg: any) {
    return (
      <div
        className={`json-error-msg md:h-[calc(80vh-151px)] md:max-h-[517px] md:min-h-[20vh] sm:max-h-[516px] pb-[16px] px-[24px] mt-[28px]
      overflow-y-auto whitespace-pre-wrap text-14 sm:px-[16px] sm:mt-[12px]
    `}
      >
        {jsonMsg ? JSON.stringify(jsonMsg, null, 2) : null}
      </div>
    );
  }

  function renderActivityErrorDetailsModal() {
    const activityData = activityErrorModalData?.activityData;
    return (
      <Modal open={true} onClose={activityErrorModalClose}>
        <div
          className={`activity-error-msg-modal absolute w-[60vw] max-w-[636px]
          sm:min-h-[260px] rounded-[8px] overflow-hidden left-1/2 -translate-x-1/2 top-1/2
          -translate-y-1/2 bg-white
          sm:top-auto sm:bottom-0 sm:left-0 sm:right-0 sm:translate-x-0 sm:translate-y-0
          sm:max-h-[90%] sm:w-full sm:rounded-b-[0]
        `}
        >
          <div className="flex items-center py-[12px] px-[24px] sm:pr-[16px] sm:pl-[4px]">
            <div
              className="rista-sprite ic-arrow-left min-w-[40px]"
              onClick={activityErrorModalClose}
            ></div>
            <p className="text-22 font-semibold sm:leading-none">
              {`${
                OUTLET_ACTIVITY_TYPES[activityData?.type] || activityData?.type
              } - Error Message`}
            </p>
          </div>
          <div
            className={`text-12 text-black/50 border-b border-[#DDDDDD] px-[24px] py-[20px]
              sm:px-[16px] sm:py-[12px]
            `}
          >
            {activityData?.userName ? `By ${activityData?.userName} ` : ""}
            {activityData?.lastModifiedDate
              ? `on ${getFormattedDateTime(activityData?.lastModifiedDate)}`
              : ""}
          </div>
          {activityErrorDetails?.extra?.response
            ? renderJsonErrorMessage(activityErrorDetails?.extra?.response)
            : null}
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={true} onClose={onModalClose}>
      <div
        className={`outlet-activities-modal absolute w-[60vw] max-w-[636px] rounded-[8px]
        overflow-hidden left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 bg-white
        sm:bottom-0 sm:right-0 sm:left-0 sm:top-auto sm:translate-x-0 sm:translate-y-0
        sm:rounded-b-[0] sm:h-auto sm:max-h-[90%] sm:w-full
      `}
      >
        <div
          className={`flex items-center justify-between py-[12px] px-[24px]
            sm:px-[16px]
          `}
        >
          <p className="text-22 font-semibold sm:leading-none">
            Activity Log - {channelDefName}
          </p>
          <div
            className="rista-sprite ic-cross-black"
            onClick={onModalClose}
          ></div>
        </div>
        <div className="activities-list md:h-[calc(80vh-64px)] md:max-h-[604px] md:min-h-[20vh] sm:max-h-[602px] pb-[16px] overflow-y-auto">
          {activityLogsList?.length
            ? activityLogsList.map((activityData: any, index : number) => {
                return renderActivityInfo(activityData, index);
              })
            : null}
        </div>
        {activityErrorModalData?.show
          ? renderActivityErrorDetailsModal()
          : null}
      </div>
    </Modal>
  );
};

export default ActivityLogModal;
